import React from 'react'
import { SquareCheck } from 'lucide-react'
import { DataTable } from 'components/DataTable'
import { CellContext, createColumnHelper } from '@tanstack/react-table'
import { formatInteger } from 'utils/formatting'
import { FbLink } from 'components/FbUI/FbLink'
import { ContactCompanySearchResult, DoorSearchResult } from 'models/companies'
import { useUnifiedSearchCompanyUrl } from 'utils/getUnifiedSearchCompanyUrl'
import { TYPE_MAPPING } from './constants'
import { OverflownText } from 'components/OverflownText'

// Union type for all search results
type SearchResult = ContactCompanySearchResult | DoorSearchResult

// Common styles for text truncation
const TRUNCATION_STYLE = {
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const

const SelectedBadge = () => (
  <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-success-bg pl-1.5 pr-2 py-0.5">
    <SquareCheck className="size-3 text-success" strokeWidth={2.5} />
    <span className="text-xs font-medium leading-4 tracking-tight text-success">
      Selected
    </span>
  </span>
)

interface UnifiedSearchTableProps {
  data: SearchResult[]
  isLoading: boolean
  selectedTab: 'firstbite' | 'unmatched' | 'firstbitedoors' | 'unmatcheddoors'
  currentTotalRows: number
  pageSize: number
  currentPage: number
  setCurrentPage: (page: number) => void
  onRowClick: (row: SearchResult) => void
  selectedItem?: SearchResult | null
  showRadioButtons?: boolean
}

export function UnifiedSearchTable({
  data,
  isLoading,
  selectedTab,
  currentTotalRows,
  pageSize,
  currentPage,
  setCurrentPage,
  onRowClick,
  selectedItem,
  showRadioButtons = false,
}: UnifiedSearchTableProps) {
  const { getFirstBiteUrl, getUnmatchedUrl, getDoorUrl } =
    useUnifiedSearchCompanyUrl()

  // Helper to render name cell with tooltip and truncation
  const renderNameCell = (name: string, url: string) => {
    const content =
      url === '#' ? (
        <span className="text-gray-500">{name}</span>
      ) : (
        <FbLink
          to={url}
          target="_blank"
          onClick={(e) => e.stopPropagation()}
          style={TRUNCATION_STYLE}
        >
          {name}
        </FbLink>
      )

    return (
      <div className="max-w-full">
        <OverflownText tooltipText={name} maxLines={1}>
          <div style={TRUNCATION_STYLE}>{content}</div>
        </OverflownText>
      </div>
    )
  }

  // Helper to determine if a row is selected
  const isRowSelected = (row: SearchResult): boolean => {
    if (!selectedItem) return false

    // Type guard to check if this is a door result
    const isDoorRow =
      'id' in row && ('door_proxy_id' in row || 'chain_id' in row)
    const isDoorSelected =
      'id' in selectedItem &&
      ('door_proxy_id' in selectedItem || 'chain_id' in selectedItem)

    // For door results, compare by id
    if (isDoorRow && isDoorSelected) {
      return row.id === selectedItem.id
    }

    // For company results (FB Companies or Custom Companies)
    // Now we're comparing against the original clicked row, so we can use item_id or contact_company_id
    const rowCompanyId = (row as any).contact_company_id
    const selectedCompanyId = (selectedItem as any).contact_company_id
    const rowItemId = (row as any).item_id
    const selectedItemId = (selectedItem as any).item_id

    // If both have item_id (FB Companies), compare by item_id
    if (rowItemId && selectedItemId) {
      return rowItemId === selectedItemId
    }

    // If both have contact_company_id (Custom Companies), compare by contact_company_id
    if (rowCompanyId && selectedCompanyId) {
      return rowCompanyId === selectedCompanyId
    }

    return false
  }

  // Helper to create radio button column
  const createRadioColumn = (columnHelper: any) => {
    return columnHelper.display({
      id: 'radio',
      header: '',
      size: 50,
      cell: (info: any) => {
        const row = info.row.original
        return (
          <div className="flex items-center justify-center">
            <input
              type="radio"
              checked={isRowSelected(row)}
              onChange={() => onRowClick(row)}
              onClick={(e) => e.stopPropagation()}
              className="w-4 h-4 cursor-pointer accent-fb-green-800"
            />
          </div>
        )
      },
    })
  }

  // Create separate column helpers for each type
  const doorColumnHelper = createColumnHelper<DoorSearchResult>()
  const companyColumnHelper = createColumnHelper<ContactCompanySearchResult>()

  // Define different columns based on selected tab
  const doorColumns = [
    ...(showRadioButtons ? [createRadioColumn(doorColumnHelper)] : []),
    doorColumnHelper.accessor('name', {
      header: 'Door Name',
      size: 250,
      cell: (info: CellContext<DoorSearchResult, string>) => {
        const door = info.row.original
        const isUnmatched = selectedTab === 'unmatcheddoors'
        const url = getDoorUrl ? getDoorUrl(door, isUnmatched) : '#'
        const selected = isRowSelected(door)
        return (
          <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1">
              {renderNameCell(door.name, url)}
            </div>
            {selected && <SelectedBadge />}
          </div>
        )
      },
    }),
    doorColumnHelper.accessor('chain_name', {
      header: 'Chain',
      size: 200,
      cell: (info) => info.getValue() || '-',
    }),
    doorColumnHelper.accessor('address', {
      header: 'Address',
      size: 250,
      cell: (info) => info.getValue() || '-',
    }),
    doorColumnHelper.accessor('city', {
      header: 'City',
      size: 120,
      cell: (info) => info.getValue() || '-',
    }),
    doorColumnHelper.accessor('state', {
      header: 'State',
      size: 80,
      cell: (info) => info.getValue() || '-',
    }),
    doorColumnHelper.accessor('zipcode', {
      header: 'Zip',
      size: 100,
      cell: (info) => info.getValue() || '-',
    }),
    doorColumnHelper.accessor('category_name', {
      header: 'Category',
      size: 150,
      cell: (info) => {
        const door = info.row.original as any
        let value = info.getValue() || door.category

        // If no category_name, try to derive from type
        // COMPANY_TYPE_UPDATE
        if (!value && door.type) {
          // Map type codes to broad categories
          const typeToCategory: Record<string, string> = {
            RE: 'Commercial',
            HO: 'Non Commercial',
            DI: 'Non Commercial',
            CU: 'Non Commercial',
            NH: 'Non Commercial',
            LG: 'Commercial',
            CS: 'Commercial',
          }
          value = typeToCategory[door.type]
        }

        return value || '-'
      },
    }),
    doorColumnHelper.accessor('sub_category_name', {
      header: 'Type',
      size: 150,
      cell: (info) => {
        const door = info.row.original as any
        let value = info.getValue() || door.subCategory || door.sub_category

        // If no sub_category_name, use TYPE_MAPPING for type codes
        if (!value && door.type && TYPE_MAPPING[door.type]) {
          value = TYPE_MAPPING[door.type]
        }

        return value || '-'
      },
    }),
  ]

  const companyColumns = [
    ...(showRadioButtons ? [createRadioColumn(companyColumnHelper)] : []),
    companyColumnHelper.accessor('name', {
      header: 'Name',
      size: 250,
      cell: (info: CellContext<ContactCompanySearchResult, string>) => {
        const company = info.row.original
        const url =
          selectedTab === 'firstbite'
            ? getFirstBiteUrl(company)
            : getUnmatchedUrl(company)
        const selected = isRowSelected(company)
        return (
          <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1">
              {renderNameCell(company.name, url)}
            </div>
            {selected && <SelectedBadge />}
          </div>
        )
      },
    }),
    companyColumnHelper.accessor('domain', {
      header: 'Domain',
      size: 200,
      cell: (info) => info.getValue() || '-',
    }),
    companyColumnHelper.display({
      id: 'size',
      header: 'Size',
      size: 100,
      cell: (info) => {
        const company = info.row.original as any
        return company.size ? formatInteger(company.size) : '-'
      },
    }),
    companyColumnHelper.display({
      id: 'category',
      header: 'Category',
      size: 150,
      cell: (info) => {
        const company = info.row.original as any
        return company.category_name || '-'
      },
    }),
    companyColumnHelper.accessor('sub_category_name', {
      header: 'Type',
      size: 150,
      cell: (info) => info.getValue() || '-',
    }),
    companyColumnHelper.display({
      id: 'state_summary',
      header: 'States',
      size: 300,
      cell: (info) => {
        const company = info.row.original as any
        const stateSummary = company.state_summary
        return stateSummary ? (
          <OverflownText maxLines={2}>{stateSummary}</OverflownText>
        ) : (
          '-'
        )
      },
    }),

    companyColumnHelper.display({
      id: 'city_summary',
      header: 'Cities',
      size: 300,
      cell: (info) => {
        const company = info.row.original as any
        const cities = company.city_summary
        if (!cities) return '-'

        const citiesSorted = cities.split(', ').sort()
        const display = citiesSorted.slice(0, 25).join(', ')
        const totalCitiesCount = citiesSorted.length
        let totalCitiesDisplay: string
        if (totalCitiesCount > 1000) {
          totalCitiesDisplay = Math.floor(totalCitiesCount / 1000) + 'k'
        } else {
          totalCitiesDisplay = totalCitiesCount.toString()
        }

        // Only show tooltip if there are more cities than displayed
        if (totalCitiesCount > 25) {
          return (
            <OverflownText tooltipText={`${cities}`} maxLines={2}>
              {`${display}, ... (${totalCitiesDisplay} total)`}
            </OverflownText>
          )
        }

        return <OverflownText maxLines={2}>{cities}</OverflownText>
      },
    }),
  ]

  // Cast columns to the proper type based on selected tab
  const columns =
    selectedTab === 'firstbitedoors' || selectedTab === 'unmatcheddoors'
      ? (doorColumns as any) // Cast door columns for DataTable
      : (companyColumns as any) // Cast company columns for DataTable

  return (
    <DataTable<SearchResult>
      tableKey="unified-search"
      data={data}
      columns={columns}
      loading={isLoading}
      enableRowSelection={false}
      virtualizeRows={false}
      isPaginationEnabled={true}
      height={430}
      autoHideScrollbar={false}
      paginationOptions={{
        totalRows: currentTotalRows,
        pageSize: pageSize,
        setPage: setCurrentPage,
        page: currentPage,
        isPaginationLoading: isLoading,
      }}
      onRowClick={(row) => onRowClick(row.original)}
    />
  )
}
